import format from 'date-fns/format';
import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  GetProfileIncludeOptions,
  ProfileAttributes,
} from '@loyalty/shared-common/profile-api-client';
import { PromocodeAttributes } from '@loyalty/shared-common/rewards-api-client';
import { colorPalette, useAlerts } from '@qualifio/ui-commons';
import { faTrophy } from '@fortawesome/pro-solid-svg-icons/faTrophy';
import { useTranslation } from 'react-i18next';
import { DeletePromocode } from './DeletePromocode';
import { ProfileDisplayName } from '../../../profiles/utils/ProfileDisplayName';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useProgram } from '../../../loyalty/ProgramContext';

function AcquiredDate({ date }: { date: Date | string }) {
  const dateObject = date instanceof Date ? date : new Date(date);
  return (
    <>
      <b>{format(dateObject, 'yyyy-MM-dd')}</b> - {format(dateObject, 'HH:mm')}
    </>
  );
}

function Profile({ profileId }: { profileId: string }) {
  const { program } = useProgram();
  const { notify } = useAlerts();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileAttributes>();

  const { profileApi } = useApi();

  useEffect(() => {
    setLoading(true);
    profileApi
      .getProfile(profileId, {
        params: { programId: program.id, include: [GetProfileIncludeOptions.memberships] },
      })
      .then(setProfile)
      .catch(error => {
        if (error?.response?.status !== 404) {
          notify(t('rewards.promo-codes.errors.profile'), 'error');
        }
      })
      .finally(() => setLoading(false));
  }, [profileId]);

  return loading ? (
    <CircularProgress />
  ) : profile?.memberships?.length ? (
    <Box sx={{ fontSize: 14 }}>
      <b>
        <ProfileDisplayName profile={profile} />
      </b>
      - <a href={`mailto:${profile.email}`}>{profile.email}</a>
    </Box>
  ) : (
    <>{t('rewards.promo-codes.errors.no-profile')}</>
  );
}

export function PromocodeListItem({
  promocode,
  onDelete,
}: {
  promocode: PromocodeAttributes;
  onDelete?: (promocode: PromocodeAttributes) => void;
}): React.ReactElement {
  const isWon = !!promocode.profileId;

  return (
    <ListItem
      component={Paper}
      sx={{
        alignItems: 'center',
        backgroundColor: !isWon ? colorPalette.grey.light : 'white',
        borderLeft: `4px solid ${isWon ? colorPalette.orange.main : colorPalette.blue.main}`,
        display: 'flex',
        marginBottom: 1,
        overflow: 'hidden',
        paddingBottom: 0,
        paddingLeft: 0,
        paddingTop: 0,
        '& a': {
          color: colorPalette.blue.main,
          textDecoration: 'none !important',
        },
      }}
    >
      <ListItemText
        sx={{
          alignItems: 'center',
          borderRight: `1px solid ${colorPalette.grey.main}`,
          display: 'flex',
          margin: 0,
          maxWidth: 230,
          minHeight: 80,
          padding: 2,
        }}
        primary={promocode.code}
        primaryTypographyProps={{
          sx: {
            color: isWon ? colorPalette.grey.main : undefined,
            textDecoration: isWon ? 'line-through' : undefined,
            fontWeight: 700,
          },
        }}
      />
      {promocode.profileId && promocode.acquiredAt && (
        <ListItemText
          sx={{ margin: 0, padding: 1 }}
          primary={<Profile profileId={promocode.profileId} />}
          secondary={
            <Box component="span" sx={{ fontSize: 14 }}>
              <AcquiredDate date={promocode.acquiredAt} />
            </Box>
          }
        />
      )}
      {!isWon && (
        <ListItemText
          sx={{ padding: 2 }}
          primary={'Reward code not won yet.'}
          primaryTypographyProps={{
            sx: { color: colorPalette.grey.dark },
          }}
        />
      )}
      <ListItemSecondaryAction>
        {isWon ? (
          <Box
            component={FontAwesomeIcon}
            sx={{
              color: isWon ? colorPalette.orange.main : colorPalette.grey.dark,
              fontSize: 16,
            }}
            icon={faTrophy}
          />
        ) : (
          <DeletePromocode
            rewardId={promocode.reward.id}
            promocodeId={promocode.id}
            onDeleted={() => onDelete?.(promocode)}
          />
        )}
      </ListItemSecondaryAction>
    </ListItem>
  );
}
